/**
 * Bilder im E-Mail-Editor (EmailComposerV2): Einfügen per Strg+V / Drag & Drop / Bild-Knopf,
 * Verkleinern großer Bilder, Startbreite im Text. Die Ziehpunkte an den Ecken liefert die
 * TipTap-Image-Erweiterung (BILD_RESIZE), die Optik steht in index.css.
 */
import { toast } from 'sonner';

// Ziehpunkte an den vier Ecken, Seitenverhältnis bleibt erhalten
export const BILD_RESIZE = {
  enabled: true,
  directions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
  minWidth: 40,
  minHeight: 20,
  alwaysPreserveAspectRatio: true,
};

const BILD_MAX_BYTES = 5 * 1024 * 1024;  // Originaldatei
const BILD_MAX_PIXEL = 1600;             // lange Kante nach Verkleinerung (hält Mails klein)
const BILD_ANZEIGE_BREITE = 600;         // Startbreite im Text, per Ecken-Ziehpunkt änderbar

export const bildDateienAus = (dt) => Array.from(dt?.files || []).filter((f) => f.type && f.type.startsWith('image/'));

/** Bilddatei → { src: data-URI, width, height }; große Bilder werden verkleinert. */
export const bildVorbereiten = (file) => new Promise((resolve) => {
  if (file.size > BILD_MAX_BYTES * 4) {
    toast.error('Bild zu groß (max. 20 MB)');
    resolve(null);
    return;
  }
  const reader = new FileReader();
  reader.onerror = () => { toast.error('Bild konnte nicht gelesen werden'); resolve(null); };
  reader.onload = () => {
    const original = String(reader.result || '');
    if (!original.startsWith('data:image/')) { resolve(null); return; }
    const img = new window.Image();
    img.onerror = () => resolve({ src: original });
    img.onload = () => {
      let w = img.naturalWidth || img.width;
      let h = img.naturalHeight || img.height;
      let src = original;
      const faktor = Math.min(1, BILD_MAX_PIXEL / Math.max(w, h));
      // Verkleinern, wenn zu groß (Pixel oder Dateigröße); GIF bleibt unverändert (Animation)
      if ((faktor < 1 || file.size > BILD_MAX_BYTES) && file.type !== 'image/gif') {
        w = Math.round(w * faktor); h = Math.round(h * faktor);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        src = file.type === 'image/png' ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.85);
      }
      const breite = Math.min(w, BILD_ANZEIGE_BREITE);
      resolve({ src, width: breite, height: Math.round(h * (breite / w)) });
    };
    img.src = original;
  };
  reader.readAsDataURL(file);
});

export const bildHtml = (bild) => `<img src="${bild.src}"${bild.width ? ` width="${bild.width}" height="${bild.height}"` : ''} style="max-width:100%;height:auto">`;

/** Bild in den TipTap-Editor einfügen (an der Auswahl bzw. an der Drop-Position). */
export const fuegeBildInView = (view, file, pos) => {
  bildVorbereiten(file).then((bild) => {
    if (!bild || !view || view.isDestroyed) return;
    const typ = view.state.schema.nodes.image;
    if (!typ) return;
    const node = typ.create(bild);
    const tr = typeof pos === 'number' ? view.state.tr.insert(pos, node) : view.state.tr.replaceSelectionWith(node);
    view.dispatch(tr.scrollIntoView());
    view.focus();
  });
};

// Bildquellen, die der Empfänger nie sehen könnte (lokale Dateien aus Word/Outlook usw.)
const UNBRAUCHBARE_BILDQUELLE = /^(?:file:|blob:|cid:|webkit-fake-url:|ms-appx|$)/i;

/**
 * Word/Outlook legen Text + Bilder als HTML in die Zwischenablage; die Bilder zeigen dort
 * auf lokale Dateien (file:///…/clip_image001.png), die Bilddaten liegen separat als Dateien bei.
 * True, wenn das HTML echten Text enthält (dann HTML + Bilder zusammen einfügen, statt nur der Bilder).
 */
export const htmlMitText = (html) => {
  if (!html) return false;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body?.textContent || '').replace(/\s+/g, '').length > 0;
};

/**
 * Ersetzt unbrauchbare <img>-Quellen im HTML der Reihe nach durch die mitgelieferten Bilddateien
 * (verkleinert, als data-URI). Übrig gebliebene unbrauchbare Bilder werden entfernt.
 * Liefert { html, fehlend } – fehlend = Anzahl entfernter Bilder.
 */
export const htmlMitEingebettetenBildern = async (html, dateien) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script,style,meta,link,title').forEach((el) => el.remove());
  const bilder = (await Promise.all((dateien || []).map(bildVorbereiten))).filter(Boolean);
  let fehlend = 0;
  doc.querySelectorAll('img').forEach((img) => {
    if (!UNBRAUCHBARE_BILDQUELLE.test((img.getAttribute('src') || '').trim())) return;
    const bild = bilder.shift();
    if (!bild) { img.remove(); fehlend += 1; return; }
    img.setAttribute('src', bild.src);
    img.removeAttribute('v:shapes');
    if (bild.width) { img.setAttribute('width', bild.width); img.setAttribute('height', bild.height); }
  });
  // Bilddateien ohne passenden Platzhalter im HTML ans Ende hängen
  const rest = bilder.map(bildHtml).join('');
  return { html: doc.body.innerHTML + rest, fehlend };
};

export const hinweisFehlendeBilder = (fehlend) => {
  if (fehlend > 0) {
    toast.warning(`${fehlend} Bild${fehlend === 1 ? '' : 'er'} aus der Zwischenablage konnte${fehlend === 1 ? '' : 'n'} nicht übernommen werden – bitte einzeln kopieren und einfügen.`);
  }
};

// Eingebettete Bilder in importierten HTML-Vorlagen: ab dieser Länge (Zeichen der data-URI) wird verkleinert
const EINGEBETTET_AB_ZEICHEN = 400 * 1024;
const EINGEBETTETES_BILD = /data:image\/(png|jpe?g|webp|bmp);base64,([A-Za-z0-9+/=\s]+)/gi;

const verkleinereDataUri = (dataUri, typ) => new Promise((resolve) => {
  const img = new window.Image();
  img.onerror = () => resolve(null);
  img.onload = () => {
    const w0 = img.naturalWidth || img.width;
    const h0 = img.naturalHeight || img.height;
    const faktor = Math.min(1, BILD_MAX_PIXEL / Math.max(w0, h0));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w0 * faktor);
    canvas.height = Math.round(h0 * faktor);
    const ctx = canvas.getContext('2d');
    if (typ !== 'png') { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height); }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const neu = typ === 'png' ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.82);
    resolve(neu.length < dataUri.length ? neu : null);  // null = unverändert lassen
  };
  img.src = dataUri;
});

/**
 * Verkleinert große, per data-URI eingebettete Bilder in einem kompletten HTML-Dokument
 * (z. B. importierte E-Mail-Vorlage). Der übrige HTML-Text bleibt unverändert.
 * Liefert { html, anzahl } – anzahl = Zahl der verkleinerten Bilder.
 */
export const verkleinereEingebetteteBilder = async (html) => {
  const treffer = [...String(html || '').matchAll(EINGEBETTETES_BILD)]
    .filter((m) => m[0].length > EINGEBETTET_AB_ZEICHEN);
  if (!treffer.length) return { html, anzahl: 0 };
  const ersatz = new Map();
  for (const m of treffer) {
    if (ersatz.has(m[0])) continue;
    const typ = m[1].toLowerCase() === 'png' ? 'png' : 'jpeg';
    ersatz.set(m[0], await verkleinereDataUri(m[0].replace(/\s+/g, ''), typ));
  }
  let anzahl = 0;
  const neu = html.replace(EINGEBETTETES_BILD, (ganz) => {
    const r = ersatz.get(ganz);
    if (!r) return ganz;
    anzahl += 1;
    return r;
  });
  return { html: neu, anzahl };
};
